import { Component, Vue } from 'vue-property-decorator';
import axios from 'axios';
import { mapDataSetAPI } from '@/providers/apis/app/map-dataset';
import { statsAPI } from '@/providers/apis/stats/stats';
import { groupsApi } from '@/providers/apis/groups/groups';
import NavLearningWorldWide from './nav-learning-worldwide/nav-learning-worldwide';
import NetworkHierarchySelection from '@/components/pull-up/hierarchy-selection/hierarchy-selection';
import Partners from './partners/partners';
import { CountryModel } from '@/models/stats/country';
import { drillDownAPI } from '@/providers/apis/drill-down/drill-down';
import { sessionService } from '@/providers/services/auth/session';
import { DEMO_USERS } from '@/utils/constants';
import { GroupHierarchy } from '@/models/groups/hierarchy';
import { getNetworkDefaultURL, isAPICatchError } from '@/utils/utils';
import { appConfigService } from '@/providers/services/app/app-config';
import { GroupCountry, CountryData } from '@/models/groups/country';
import moment from 'moment';


@Component({
  components: {
    'nav-learning-worldwide': NavLearningWorldWide,
    'network-hierarchy-selection': NetworkHierarchySelection,
    'partners': Partners,
  },
})
export default class Network extends Vue {

  private groupCountries: CountryData[] = [];

  get session() {
    return sessionService.getSession();
}

  get isTenant() {
     return this.$access.hasPermission(this.$access.menus.network, this.$access.ACL.all)
              || this.$access.hasPermission(this.$access.menus.network, this.$access.ACL.partner);
  }

  get isSuperAdmin() {
    return this.$access.hasPermission(this.$access.menus.all, this.$access.ACL.all);
  }

  private get isDrilldown() {
    return this.$access.hasPermission(this.$access.menus.network, this.$access.ACL.compDrilldown)
              || this.$access.hasPermission(this.$access.menus.network, this.$access.ACL.compDrillAnalytic);
  }

  // -------------------------------------------------------------------------
  // Properties

  /**
   * Maintains the data of map plotting values
   * @type {Object}
   */
  private mapData: any = null;

  /**
   * Showing UI after the country API loaded
   */
  private isLoading: boolean = false;

  private isShowHierarcySelection: boolean = false;

  private hierarchyId: number = 0;

  private activeMonth: string = moment().format('M');

  private activeYear: string = moment().format('YYYY');


private get districtLevel() {
    const sessionName = this.session!.username || '';
    return ['bmcclintockad', 'bmcclintockcc', 'districtadmin', 'curriculumcoord'].indexOf(sessionName) !== -1;
}

private get schoolLevel() {
    const sessionName = this.session!.username || '';
    return ['bmcclintocksp', 'schoolprincipal'].indexOf(sessionName) !== -1;
}

  private selectedGroupCountry!: CountryData | undefined;

  private groupHierarchies: any[] = [];

  // -------------------------------------------------------------------------
  // Methods

  public fetchNavWorldWideMapData() {
    const overallStats = {
      totalStudentsCount: 0,
      totalTeachersCount: 0,
      totalOthersCount: 0,
    };
    const countryService = statsAPI.getCountries();
    return axios.all([
      mapDataSetAPI.getCountries(),
      countryService,
      mapDataSetAPI.getCountriesRegion(),
    ])
      .then(axios.spread((countries, statsCountries, countriesRegion) => {
        if (statsCountries) {
          statsCountries = this.isSuperAdmin
              ? statsCountries : this.groupCountries;
          statsCountries.map((statsCountry: any) => {
            const countryCode = this.isSuperAdmin ?  statsCountry.country_code : statsCountry.code;
            const country = countries.features.find((countryData: any) => {
              return countryCode === countryData.country_code;
            });
            if (country) {
              country.has_data = true;
              country.total_students = statsCountry.total_students;
              country.total_teachers = statsCountry.total_teachers;
              country.total_others = statsCountry.total_others;
              country.total_users = statsCountry.total_users;
              country.total_classes = statsCountry.total_classes;
              country.total_competencies_gained = statsCountry.total_competencies_gained;
              country.total_timespent = statsCountry.total_timespent;
              country.total_activities_conducted = statsCountry.total_activities_conducted;
              country.total_navigator_courses = statsCountry.total_navigator_courses;
              country.country_id = statsCountry.id;
              country.country_name = this.isSuperAdmin ? statsCountry.country_name : statsCountry.name;

              // Calculate the overall count of Student, Teachers and Others
              overallStats.totalStudentsCount += statsCountry.total_students;
              overallStats.totalTeachersCount += statsCountry.total_teachers;
              overallStats.totalOthersCount += statsCountry.total_others;

              const countryRegion = countriesRegion.find((region: any) => {
                return region.country_code === countryCode;
              });
              if (countryRegion) {
                country.latitude = countryRegion.latitude;
                country.longitude = countryRegion.longitude;
              }
            }
          });
        }
        return {
          countries,
          statsCountries,
          overallStats,
        };
      }));
  }



  // -------------------------------------------------------------------------
  // Hooks

  private created() {
    this.isLoading = true;
    this.$access.deleteClassDrillDown();
    this.loadHierarchyData().then(() => {
      const worldMapDataSet = this.fetchNavWorldWideMapData();
      worldMapDataSet.then((data) => {
        this.mapData = data;
      });
    });

  }

  private onSelectCountry(countryData: any) {

    const selectedCountryHierarchies = this.groupCountries.find(
        (groupCountry) => groupCountry.id === countryData.country_id);

    const isHasMultipleTenants = false;
    // !(this.groupHierarchies &&
    //  this.groupHierarchies.length === 1 &&
    //  this.groupHierarchies[0].tenants &&
    //  this.groupHierarchies[0].tenants.length === 1);
    if (isHasMultipleTenants) {
      this.selectedGroupCountry = selectedCountryHierarchies;
      this.isShowHierarcySelection = true;
    } else {
      if (selectedCountryHierarchies) {
        this.hierarchyId = selectedCountryHierarchies.hierarchies[0].id;
        if (selectedCountryHierarchies.hierarchies[0].tenants) {
          this.$access.setGroupHierarchyTenants(JSON.stringify(selectedCountryHierarchies.hierarchies[0].tenants[0]));
        }
      }
      const selectedHierarcy = this.groupHierarchies.find((groupHierarchy) => groupHierarchy.id === this.hierarchyId);
      appConfigService.setUserGroupAccessLevel(selectedHierarcy.levels);
      const params = {
        groupType: 'country',
        name: this.$access.isDemoGroup ? 'World' : countryData.country_name,
      };
      const query = {
        groupId: countryData.country_id,
        hierarchyId: this.hierarchyId,
        selectedLevel: 'country',
      };
      this.$router.push(
        getNetworkDefaultURL(params, query),
      );
    }
  }

  private loadHierarchyData() {
    return new Promise((resolve, reject) => {
      axios.all([groupsApi.fetchUserHierarchies(),
      groupsApi.fetchGroupCountries()]).then(axios.spread((groupHierarchies, groupCountry) => {
        if (isAPICatchError(groupHierarchies) || isAPICatchError(groupCountry)) {
          this.isLoading = false;
          this.$router.push('/error');
        } else {
          this.groupCountries = groupCountry.data;
          this.groupHierarchies = groupHierarchies;
          this.$access.getSetHierarchyDetails(groupHierarchies);
          if (!this.$access.isDemoTenant) {
            this.activeMonth = moment().format('M');
            this.activeYear = moment().format('YYYY');
          } else {
            this.activeMonth = '4';
            this.activeYear = '2020';
          }
          if (groupHierarchies.length === 1 || !this.$access.isDemoTenant) {
            const defaultHierarchy = groupHierarchies[0];
            appConfigService.setUserGroupAccessLevel(defaultHierarchy.levels);
            this.hierarchyId = defaultHierarchy.id;
            const groupsReportParam = {
              hierarchyId: defaultHierarchy.id,
              tenants: defaultHierarchy.tenants ? defaultHierarchy.tenants.map(
                (tenant: any) => tenant.id).join() : undefined,
              month: parseInt(this.activeMonth, 10),
              year: parseInt(this.activeYear, 10),
            };
            if (!this.$access.isDemoTenant || this.$access.isDemoGroup) {
              this.$access.setGroupHierarchyTenants(JSON.stringify(defaultHierarchy.tenants[0]));
              groupsApi.fetchGroupReport(groupsReportParam).then((groupsReport) => {
                if (isAPICatchError(groupsReport)) {
                  this.$router.push('/error');
                } else {
                  // When don't have access to multiple countries
                  if (groupsReport.data.length <= 1) {
                    const defaultLevelData = groupsReport.data[0];
                    const params = {
                      groupType: defaultLevelData.type,
                      name: this.$access.isDemoGroup ? 'World' : defaultLevelData.name,
                    };
                    const query = {
                      groupId: defaultLevelData.id,
                      hierarchyId: this.hierarchyId,
                      selectedLevel: defaultLevelData.type,
                    };
                    this.$router.push(
                      getNetworkDefaultURL(params, query, true),
                    );
                  } else {
                    this.isLoading = false;
                  }
                }
              });

            } else {
              this.isLoading = false;
            }
          } else {
            this.isLoading = false;
          }
          resolve(groupCountry.data);
        }
      }), (error) => {
        this.isLoading = false;
      });
    });


  }

  private loadGroupCountries() {
    groupsApi.fetchGroupCountries().then((groupCountry: GroupCountry) => {
      this.groupCountries = groupCountry.data;
    });
  }

}
